import type { FeedbackDetail, FeedbackItem, Pin, Submission, ThreadMessage } from './types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function parse<T>(response: Response): Promise<T> {
  if (response.ok) return (await response.json()) as T;
  let message = `Request failed (${response.status})`;
  try {
    const body = (await response.json()) as { error?: string };
    if (body.error) message = body.error;
  } catch {
    // Body was not JSON; keep the generic message.
  }
  throw new ApiError(message, response.status);
}

export interface ApiClient {
  submit(submission: Submission): Promise<FeedbackItem>;
  listMine(email: string): Promise<FeedbackItem[]>;
  /** Unread team replies, and the name the team set for this tester (null if none). */
  unread(email: string): Promise<{ unread: number; name: string | null }>;
  detail(email: string, id: string): Promise<FeedbackDetail>;
  reply(email: string, id: string, body: string): Promise<ThreadMessage>;
  pins(pageUrl: string, email: string | null): Promise<Pin[]>;
}

export function createApiClient(endpoint: string, project: string): ApiClient {
  const base = `${endpoint}/v1/projects/${encodeURIComponent(project)}`;
  const as = (email: string, init: RequestInit = {}): RequestInit => ({
    ...init,
    headers: {
      'x-reporter-email': email,
      ...(init.body ? { 'content-type': 'application/json' } : {}),
    },
  });

  return {
    async submit(submission) {
      const response = await fetch(`${base}/feedback`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(submission),
      });
      return parse<FeedbackItem>(response);
    },
    async listMine(email) {
      return (await parse<{ items: FeedbackItem[] }>(await fetch(`${base}/feedback`, as(email))))
        .items;
    },
    async unread(email) {
      const res = await parse<{ unread: number; name?: string | null }>(
        await fetch(`${base}/unread`, as(email)),
      );
      return { unread: res.unread, name: res.name ?? null };
    },
    async detail(email, id) {
      return parse<FeedbackDetail>(
        await fetch(`${base}/feedback/${encodeURIComponent(id)}`, as(email)),
      );
    },
    async reply(email, id, body) {
      const response = await fetch(
        `${base}/feedback/${encodeURIComponent(id)}/messages`,
        as(email, { method: 'POST', body: JSON.stringify({ body }) }),
      );
      return parse<ThreadMessage>(response);
    },
    async pins(pageUrl, email) {
      const response = await fetch(
        `${base}/pins?page=${encodeURIComponent(pageUrl)}`,
        email ? as(email) : {},
      );
      return (await parse<{ pins: Pin[] }>(response)).pins;
    },
  };
}
